import { Popover } from '@headlessui/react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Time } from '@internationalized/date';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

interface Props {
  value: Time | null;
  onChange: (time: Time) => void;
  isDisabled?: boolean;
}

const pad = (value: number) => String(value).padStart(2, '0');
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

interface ColumnProps {
  label: string;
  heading: string;
  options: number[];
  selected?: number;
  onSelect: (option: number) => void;
}

function TimeColumn(props: ColumnProps) {
  const { label, heading, options, selected, onSelect } = props;
  const columnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const column = columnRef.current;
    const option = column?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (column && option) {
      column.scrollTop =
        option.offsetTop - column.clientHeight / 2 + option.clientHeight / 2;
    }
  }, []);

  return (
    <div>
      <div
        aria-hidden="true"
        className="border-b px-1 pb-1 text-center text-xs font-medium text-gray-500 dark:border-gray-600 dark:text-gray-400"
      >
        {heading}
      </div>
      <div
        ref={columnRef}
        role="group"
        aria-label={label}
        className="relative mt-1 h-56 overflow-y-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={option === selected}
            onClick={() => onSelect(option)}
            className={clsx(
              'block w-12 rounded px-2 py-1 text-center text-sm tabular-nums',
              option === selected
                ? 'bg-primary-500 text-white'
                : 'dark:text-dark-text text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {pad(option)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TimePickerPopover(props: Props) {
  const { value, onChange, isDisabled } = props;
  const time = value ?? new Time(0, 0);

  return (
    <Popover>
      <Popover.Button
        disabled={isDisabled}
        aria-label="Choose time"
        className="focus-visible:ring-primary-600 ml-1 flex items-center rounded outline-none focus-visible:ring-2"
      >
        <ClockIcon className="h-4 w-4" />
      </Popover.Button>
      <Popover.Panel className="dark:bg-dark-bg absolute right-0 top-full z-20 mt-1 flex divide-x rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:divide-gray-600 dark:ring-gray-600">
        {({ close }) => (
          <>
            <TimeColumn
              label="Hour"
              heading="HH"
              options={HOURS}
              selected={value?.hour}
              onSelect={(hour) => onChange(time.set({ hour }))}
            />
            <TimeColumn
              label="Minute"
              heading="MM"
              options={MINUTES}
              selected={value?.minute}
              onSelect={(minute) => {
                onChange(time.set({ minute }));
                close();
              }}
            />
          </>
        )}
      </Popover.Panel>
    </Popover>
  );
}

export default TimePickerPopover;
